<nz-tabset [(nzSelectedIndex)]="activeTabIndex">
  <!-- Regular Services Tab -->
  <nz-tab [nzTitle]="'services.tabs.regular' | translate">
    <ng-template #header>
      <form
        nz-form
        [nzLayout]="'inline'"
        [formGroup]="searchForm"
        class="flex flex-wrap gap-2 items-center"
      >
        <beautyapp-input
          formControlName="search"
          placeholder="{{ 'services.search.searchWord' | translate }}"
          prefixIcon="search"
          clearable
          style="width: 200px;"
        />
        <beautyapp-select
          placeholder="{{ 'COLUMNS.category' | translate }}"
          [data]="categories()"
          displayMember="name"
          valueMember="id"
          formControlName="serviceCategoryId"
          style="width: 200px;"
          clearable
        />
        <beautyapp-select
          placeholder="{{ 'services.form.serviceType' | translate }}"
          [data]="serviceTypes"
          displayMember="label"
          valueMember="value"
          formControlName="serviceType"
          style="width: 150px;"
        />
        <beautyapp-select
          placeholder="{{ 'COLUMNS.employee' | translate }}"
          [data]="employees()"
          displayMember="fullName"
          valueMember="id"
          [ngModel]="selectedEmployeeId()"
          (ngModelChange)="selectedEmployeeId.set($event)"
          [ngModelOptions]="{standalone: true}"
          style="width: 200px;"
          clearable
        />
        <beautyapp-select
          placeholder="{{ 'COLUMNS.room' | translate }}"
          [data]="rooms()"
          displayMember="name"
          valueMember="id"
          [ngModel]="selectedRoomId()"
          (ngModelChange)="selectedRoomId.set($event)"
          [ngModelOptions]="{standalone: true}"
          style="width: 200px;"
          clearable
        />
        <button
          nz-button
          nzType="default"
          nzSize="small"
          type="button"
          (click)="clearFilters()"
        >
          <span nz-icon nzType="clear" nzTheme="outline"></span>
          {{ 'buttons.clear' | translate }}
        </button>
      </form>
    </ng-template>

    <nz-table
      [nzTitle]="header"
      nzShowSizeChanger
      [nzData]="services()"
      [nzFrontPagination]="false"
      [nzLoading]="loading()"
      [nzTotal]="total()"
      [nzPageSize]="pageSize()"
      [nzPageIndex]="page()"
      (nzQueryParams)="paginatorChange($event)"
      [nzPageSizeOptions]="[5, 10, 20, 50, 100]"
      >
      <thead>
        <tr>
          <th>{{ 'services.column.title' | translate }}</th>
          <th>{{ 'services.column.price' | translate }}</th>
          <th>{{ 'services.column.time' | translate }}</th>

          <th>{{ 'services.column.category' | translate }}</th>
          <th class="transaction-delete-td"></th>
        </tr>
      </thead>
      <tbody>
        @for (row of services(); track $index) {
          <tr>
            <td>
              <nz-space>
                <div *nzSpaceItem>{{ row?.name }}</div>
                <div *nzSpaceItem>@if (row?.onlineBooking) {
                  <span nz-icon nzType="global" nzTheme="outline"></span>
                }</div>
              </nz-space>
            </td>
            <td>
              <div class="flex gap-1 items-center">
                @if (row.serviceType === 'MINUTE') {
                  {{ 'services.form.minute-price' | translate }}:
                }
                @let empPrice = getEmployeePrice(row);
                @if (empPrice !== null) {
                  <span class="font-medium">{{ empPrice }} <i class="lari lari-c"></i></span>
                  @if (+empPrice !== +row?.price) {
                    <del class="text-gray-400 text-xs">{{ +row?.price }} <i class="lari lari-c"></i></del>
                  }
                } @else if (row.discountPrice > 0) {
                  <div>{{ +row?.discountPrice }} <i class="lari lari-c"></i></div>
                  <del class="text-red-500">{{ +row?.price }} <i class="lari lari-c"></i></del>
                } @else {
                  {{ +row?.price }} <i class="lari lari-c"></i>
                }
                @if (row.employeeServices?.length) {
                  <span
                    nz-popover
                    [nzPopoverContent]="employeePricesTpl"
                    nzPopoverPlacement="right"
                    class="cursor-pointer text-blue-500 ml-1"
                  >
                    <span nz-icon nzType="team" nzTheme="outline" style="font-size: 14px;"></span>
                  </span>
                  <ng-template #employeePricesTpl>
                    <div class="flex flex-col gap-1" style="max-height: 200px; overflow-y: auto;">
                      @for (es of row.employeeServices; track es.employeeId) {
                        <div class="flex justify-between gap-4 text-sm"
                          [class.font-bold]="es.employeeId === employeeId()"
                        >
                          <span>{{ es.employeeName }}</span>
                          <span>{{ +es.price }} <i class="lari lari-c"></i></span>
                        </div>
                      }
                    </div>
                  </ng-template>
                }
              </div>
            </td>
            <td>
              @if (row.serviceType === 'STANDARD') {
                {{ row?.time }}
              }
            </td>
            <td>
              <nz-tag [nzColor]="row?.serviceCategory?.color">{{
                row?.serviceCategory?.name
                }}
              </nz-tag>
            </td>
            <td>
              <button
                (click)="serviceSelected.emit(row)"
                nz-button
                nzType="primary"
                nzShape="circle"
                >
                <nz-icon nzType="right" nzTheme="outline" />
              </button>
            </td>
          </tr>
        }
      </tbody>
    </nz-table>
  </nz-tab>

  <!-- Package Services Tab -->
  <nz-tab [nzTitle]="packagesTabTitle">
    <ng-template #packagesTabTitle>
      <span>{{ 'services.tabs.packages' | translate }}</span>
      @if (packageServices().length > 0) {
        <nz-tag nzColor="cyan" class="ml-2">{{ packageServices().length }}</nz-tag>
      }
    </ng-template>

    <!-- Inline package purchase: opens a dialog inside this modal so the
         operator can sell a new package to the current customer without
         abandoning the booking. On successful sale, the package balance
         is auto-refreshed and the new package appears below. -->
    <div class="flex justify-end mb-3">
      <button
        nz-button
        nzType="primary"
        type="button"
        (click)="openPackagePurchase()"
        [disabled]="!userId()"
      >
        <i nz-icon nzType="shopping-cart" nzTheme="outline"></i>
        პაკეტის შეძენა
      </button>
    </div>

    <nz-table
      #packageTable
      [nzData]="packageServices()"
      [nzLoading]="packageServicesLoading()"
      [nzFrontPagination]="true"
      [nzPageSize]="10"
      nzShowSizeChanger
      [nzPageSizeOptions]="[5, 10, 20, 50]"
    >
      <thead>
        <tr>
          <th>{{ 'services.column.title' | translate }}</th>
          <th>{{ 'services.packages.packageName' | translate }}</th>
          <th>{{ 'services.offers.remaining' | translate }}</th>
          <th>{{ 'services.column.price' | translate }}</th>
          <th>{{ 'services.offers.expires' | translate }}</th>
          <th class="transaction-delete-td"></th>
        </tr>
      </thead>
      <tbody>
        @for (pkg of packageTable.data; track pkg.packageItemId) {
          <tr>
            <td>
              <div>
                <div>{{ pkg.serviceName }}</div>
                <div class="text-gray-500 text-sm">{{ pkg.categoryName }}</div>
              </div>
            </td>
            <!--
              An individual package's name is its service list joined together, so it can be
              long. .ant-tag sets white-space: nowrap, which made the cell stretch to the full
              string and pushed the whole table past the modal's right edge, hiding the expiry
              column and the select button (beaut-398). Let it wrap and cap the column instead.
            -->
            <td class="max-w-[320px]">
              <nz-tag nzColor="cyan" class="!whitespace-normal !h-auto !leading-snug">{{
                pkg.packageName
              }}</nz-tag>
            </td>
            <td>
              <nz-tag [nzColor]="pkg.remainingQuantity <= 1 ? 'red' : 'green'">
                {{ pkg.remainingQuantity }}/{{ pkg.totalQuantity }}
              </nz-tag>
            </td>
            <td>
              <div class="flex flex-col">
                @if (pkg.originalPrice !== pkg.discountedPrice) {
                  <del class="text-gray-400">{{ pkg.originalPrice }} <i class="lari lari-c"></i></del>
                }
                <span class="text-green-600 font-medium">{{ pkg.discountedPrice }} <i class="lari lari-c"></i></span>
                <span class="text-xs text-gray-500">{{ 'services.offers.prepaid' | translate }}</span>
              </div>
            </td>
            <td>
              <span [class.text-red-500]="isExpiringSoon($any(pkg.validUntil))">
                {{ pkg.validUntil | date:'dd/MM/yyyy' }}
              </span>
            </td>
            <td>
              <button
                (click)="packageServiceSelected.emit(pkg)"
                nz-button
                nzType="primary"
                nzShape="circle"
                [disabled]="pkg.remainingQuantity <= 0"
              >
                <nz-icon nzType="right" nzTheme="outline" />
              </button>
            </td>
          </tr>
        } @empty {
          <tr>
            <td colspan="6" class="text-center text-gray-500 py-8">
              {{ 'services.packages.noPackages' | translate }}
            </td>
          </tr>
        }
      </tbody>
    </nz-table>
  </nz-tab>

  <!-- Offer Services Tab -->
  <nz-tab [nzTitle]="offersTabTitle">
    <ng-template #offersTabTitle>
      <span>{{ 'services.tabs.offers' | translate }}</span>
      @if (offerServices().length > 0) {
        <nz-tag nzColor="gold" class="ml-2">{{ offerServices().length }}</nz-tag>
      }
    </ng-template>

    <nz-table
      [nzData]="offerServices()"
      [nzLoading]="offerServicesLoading()"
      [nzFrontPagination]="true"
      [nzPageSize]="10"
      nzShowSizeChanger
      [nzPageSizeOptions]="[5, 10, 20, 50]"
    >
      <thead>
        <tr>
          <th>{{ 'services.column.title' | translate }}</th>
          <th>{{ 'services.offers.offerName' | translate }}</th>
          <th>{{ 'services.offers.remaining' | translate }}</th>
          <th>{{ 'services.column.price' | translate }}</th>
          <th>{{ 'services.offers.expires' | translate }}</th>
          <th class="transaction-delete-td"></th>
        </tr>
      </thead>
      <tbody>
        @for (offer of offerServices(); track offer.offerServiceId) {
          <tr>
            <td>
              <div>
                <div>{{ offer.serviceName }}</div>
                <div class="text-gray-500 text-sm">{{ offer.categoryName }}</div>
              </div>
            </td>
            <!-- Same unwrappable-tag overflow as the packages tab above (beaut-398). -->
            <td class="max-w-[320px]">
              <nz-tag nzColor="gold" class="!whitespace-normal !h-auto !leading-snug">{{
                offer.offerName
              }}</nz-tag>
            </td>
            <td>
              <div class="flex flex-col gap-1">
                <div class="flex items-center gap-1">
                  <nz-tag [nzColor]="offer.remainingQuantity <= 1 ? 'red' : 'green'">
                    {{ offer.remainingQuantity }}/{{ offer.totalQuantity }}
                  </nz-tag>
                  <span class="text-xs text-gray-500">{{ 'services.offers.left' | translate }}</span>
                </div>
                @if (offer.usedQuantity > 0) {
                  <span class="text-xs text-orange-500">
                    {{ offer.usedQuantity }} {{ 'services.offers.reserved' | translate }}
                  </span>
                }
              </div>
            </td>
            <td>
              <div class="flex flex-col">
                @if (offer.originalPrice !== offer.discountedPrice) {
                  <del class="text-gray-400">{{ offer.originalPrice }} <i class="lari lari-c"></i></del>
                }
                <span class="text-green-600 font-medium">{{ offer.discountedPrice }} <i class="lari lari-c"></i></span>
                <span class="text-xs text-gray-500">{{ 'services.offers.prepaid' | translate }}</span>
              </div>
            </td>
            <td>
              <span [class.text-red-500]="isExpiringSoon(offer.expiryDate)">
                {{ offer.expiryDate | date:'dd/MM/yyyy' }}
              </span>
            </td>
            <td>
              <button
                (click)="offerServiceSelected.emit(offer)"
                nz-button
                nzType="primary"
                nzShape="circle"
                [disabled]="offer.remainingQuantity <= 0"
              >
                <nz-icon nzType="right" nzTheme="outline" />
              </button>
            </td>
          </tr>
        } @empty {
          <tr>
            <td colspan="6" class="text-center text-gray-500 py-8">
              {{ 'services.offers.noOffers' | translate }}
            </td>
          </tr>
        }
      </tbody>
    </nz-table>
  </nz-tab>
</nz-tabset>
